import { useEffect, useRef } from 'react';
import { sendInput, type InputEvent, type KeyModifiers, type MouseButton } from './protocol';

interface UseDesktopInputOpts {
  /** The `<video>` element receiving the remote stream. Coordinates are normalized relative to it. */
  videoEl: HTMLVideoElement | null;
  /** Bidirectional DataChannel for input. May be null until the peer is connected. */
  inputChannel: RTCDataChannel | null;
  /** Disable input capture (e.g. while still negotiating). */
  enabled: boolean;
}

/**
 * Captures mouse + keyboard from the local browser and forwards them as InputEvent JSON over the
 * input DataChannel.
 *
 * Mouse-move is rAF-throttled to avoid saturating the channel with 1000Hz polling events on
 * gaming-grade mice — at most one move event per repaint.
 *
 * Keyboard events use the DOM `code` (physical key, layout-independent). The host's
 * scancode-based SendInput injection means the same payload works on US-QWERTY, AZERTY, RU,
 * etc. — the remote OS applies its own layout.
 *
 * Browser shortcuts (Ctrl+T, Ctrl+W, F5...) are NOT preventDefault-able when running outside an
 * installed PWA; users on the Web should install the PWA for full keyboard transparency.
 * Common system keys like F11 and Ctrl+Tab will still escape to the local browser.
 */
export function useDesktopInput({ videoEl, inputChannel, enabled }: UseDesktopInputOpts): void {
  const channelRef = useRef(inputChannel);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    channelRef.current = inputChannel;
  }, [inputChannel]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // ---------- Mouse ----------
  useEffect(() => {
    if (!videoEl) return;

    // Normalize the event coordinate to the video frame's actual content rectangle (object-contain
    // can letterbox/pillarbox; we account for that by intersecting client rect with intrinsic AR).
    const normalize = (ev: MouseEvent): { x: number; y: number } | null => {
      const rect = videoEl.getBoundingClientRect();
      const vw = videoEl.videoWidth;
      const vh = videoEl.videoHeight;
      if (vw === 0 || vh === 0 || rect.width === 0 || rect.height === 0) return null;
      // Actual displayed video rect inside the element (object-contain math).
      const scale = Math.min(rect.width / vw, rect.height / vh);
      const dispW = vw * scale;
      const dispH = vh * scale;
      const offsetX = (rect.width - dispW) / 2;
      const offsetY = (rect.height - dispH) / 2;
      const localX = ev.clientX - rect.left - offsetX;
      const localY = ev.clientY - rect.top - offsetY;
      if (localX < 0 || localY < 0 || localX > dispW || localY > dispH) return null;
      return { x: localX / dispW, y: localY / dispH };
    };

    // rAF throttle for moves.
    let pendingMove: { x: number; y: number } | null = null;
    let rafId = 0;
    const flushMove = () => {
      rafId = 0;
      if (!pendingMove) return;
      const ev: InputEvent = { type: 'mouse_move', x: pendingMove.x, y: pendingMove.y };
      pendingMove = null;
      sendInput(channelRef.current, ev);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!enabledRef.current) return;
      const n = normalize(e);
      if (!n) return;
      pendingMove = n;
      if (rafId === 0) rafId = window.requestAnimationFrame(flushMove);
    };

    const buttonOf = (code: number): MouseButton | null => {
      if (code === 0) return 'left';
      if (code === 1) return 'middle';
      if (code === 2) return 'right';
      return null;
    };

    const onMouseDownUp = (e: MouseEvent, pressed: boolean) => {
      if (!enabledRef.current) return;
      const button = buttonOf(e.button);
      if (!button) return;
      const n = normalize(e);
      if (!n) return;
      e.preventDefault();
      // Cancel any pending move — we want the click position to be authoritative.
      pendingMove = null;
      sendInput(channelRef.current, { type: 'mouse_button', button, pressed, x: n.x, y: n.y });
    };

    const onContextMenu = (e: MouseEvent) => {
      // Prevent the local browser's context menu so right-clicks are transparent on the remote.
      if (enabledRef.current) e.preventDefault();
    };

    const onWheel = (e: WheelEvent) => {
      if (!enabledRef.current) return;
      // Normalize deltas to "lines": browsers report deltaY in pixels, lines, or pages depending on
      // mode. Convert everything to a single signed integer-ish scalar.
      const factor = e.deltaMode === 1 ? 1.0 : e.deltaMode === 2 ? 10.0 : 1.0 / 100.0;
      const dx = e.deltaX * factor;
      const dy = e.deltaY * factor;
      if (dx === 0 && dy === 0) return;
      e.preventDefault();
      sendInput(channelRef.current, { type: 'wheel', dx, dy });
    };

    videoEl.addEventListener('mousemove', onMouseMove);
    videoEl.addEventListener('mousedown', (e) => onMouseDownUp(e, true));
    videoEl.addEventListener('mouseup', (e) => onMouseDownUp(e, false));
    videoEl.addEventListener('contextmenu', onContextMenu);
    videoEl.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      videoEl.removeEventListener('mousemove', onMouseMove);
      videoEl.removeEventListener('contextmenu', onContextMenu);
      videoEl.removeEventListener('wheel', onWheel);
      if (rafId !== 0) window.cancelAnimationFrame(rafId);
    };
  }, [videoEl]);

  // ---------- Keyboard ----------
  useEffect(() => {
    const mods = (e: KeyboardEvent): KeyModifiers => ({
      shift: e.shiftKey || undefined,
      ctrl: e.ctrlKey || undefined,
      alt: e.altKey || undefined,
      meta: e.metaKey || undefined,
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (!enabledRef.current) return;
      // Don't intercept browser-level emergency keys (F5 = reload, F11 = fullscreen, F12 = devtools).
      if (e.code === 'F5' || e.code === 'F11' || e.code === 'F12') return;
      // Repeats arrive via OS auto-repeat; forward each one so remote auto-repeat tracks the user.
      e.preventDefault();
      sendInput(channelRef.current, { type: 'key_down', code: e.code, modifiers: mods(e) });
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (!enabledRef.current) return;
      if (e.code === 'F5' || e.code === 'F11' || e.code === 'F12') return;
      e.preventDefault();
      sendInput(channelRef.current, { type: 'key_up', code: e.code, modifiers: mods(e) });
    };

    // When the user switches tabs or alt-tabs out of the browser, modifier keys still held at
    // that moment never fire their `keyup` (the browser loses focus before the event arrives).
    // Without compensation, the remote OS sees Shift / Ctrl / Alt / Meta stuck "down" — every
    // subsequent character becomes uppercase / Ctrl-shortcut / etc.
    // On blur / visibility-hidden, send key_up for all four modifiers so the remote is clean.
    const STICKY_CODES = ['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight'];
    const releaseStickyModifiers = () => {
      for (const code of STICKY_CODES) {
        sendInput(channelRef.current, { type: 'key_up', code, modifiers: {} });
      }
    };
    const onBlur = () => releaseStickyModifiers();
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') releaseStickyModifiers();
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    window.addEventListener('keyup', onKeyUp, { capture: true });
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      window.removeEventListener('keyup', onKeyUp, { capture: true });
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
